import { useMutation } from '@base-app/web2'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_CLIENT, UPDATE_CLIENT } from '../../../graphql/clients/mutations'
import { CLIENT_QUERY } from '../../../graphql/clients/queries'
import type { Client } from '../../../graphql/clients/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import { Button, FormActions } from '../../atoms'
import { FormField, FormLoadState, TextareaField } from '../../molecules'

interface ClientFormFieldsProps {
  client?: Client
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

function derivePath(name: string) {
  return name.toLowerCase().replace(/\W+/g, '-').replace(/^-|-$/g, '')
}

export const ClientFormFields = memo(
  ({ client, onSuccess, onClose, skeleton }: ClientFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!client
    const [name, setName] = useState(client?.name ?? '')
    const [path, setPath] = useState(client?.path ?? '')
    const [pathManuallyEdited, setPathManuallyEdited] = useState(false)
    const [observations, setObservations] = useState(client?.observations ?? '')
    const [errors, setErrors] = useState<{ name?: string; path?: string }>({})

    const [createClient, { loading: creating }] = useMutation(CREATE_CLIENT, {
      optimisticResponse: {
        createClient: {
          __typename: 'Client',
          id: `temp-${Date.now()}`,
          name,
          path,
          blocked: false,
          observations: observations || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      },
      update(cache, result) {
        const newClient = result.data?.createClient
        if (!newClient) {
          return
        }
        prependToLists(cache, 'clients', newClient)
      },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const [updateClient, { loading: updating }] = useMutation(UPDATE_CLIENT, {
      optimisticResponse: {
        updateClient: {
          __typename: 'Client',
          id: client?.id ?? '',
          name,
          path,
          blocked: client?.blocked ?? false,
          observations: observations || null,
          created_at: client?.created_at ?? null,
          updated_at: new Date().toISOString(),
        },
      },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating

    const handlePathChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setPath(e.target.value)
      setPathManuallyEdited(true)
    }, [])

    const handleNameChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
        if (!isEdit && !pathManuallyEdited) {
          setPath(derivePath(e.target.value))
        }
      },
      [isEdit, pathManuallyEdited],
    )

    const validate = useCallback(() => {
      const errs: { name?: string; path?: string } = {}
      if (!name.trim()) {
        errs.name = t('clients.form.nameRequired')
      }
      if (!path.trim()) {
        errs.path = t('clients.form.pathRequired')
      } else if (!/^[a-z0-9-]+$/.test(path)) {
        errs.path = t('clients.form.pathKebabCase')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [name, path, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        if (isEdit) {
          updateClient({
            variables: { id: client.id, name, path, observations: observations || null },
          })
        } else {
          createClient({ variables: { name, path, observations: observations || null } })
        }
      },
      [validate, isEdit, client?.id, name, path, observations, updateClient, createClient],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="client-name"
          label={t('clients.form.nameLabel')}
          name="name"
          value={name}
          onChange={handleNameChange}
          error={errors.name}
          required
        />
        <FormField
          skeleton={skeleton}
          id="client-path"
          label={t('clients.form.pathLabel')}
          name="path"
          value={path}
          onChange={handlePathChange}
          error={errors.path}
        />
        <TextareaField
          skeleton={skeleton}
          id="client-observations"
          label={t('clients.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('clients.form.saving') : t('clients.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

ClientFormFields.displayName = 'ClientFormFields'

interface ClientFormProps extends Omit<ClientFormFieldsProps, 'client'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const ClientForm = memo(({ id, ...props }: ClientFormProps) => {
  const { record, status } = useRecordById(CLIENT_QUERY, id, data => data.client)
  if (status === 'loading') {
    return <ClientFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <ClientFormFields key={id ?? 'new'} client={record} {...props} />
})

ClientForm.displayName = 'ClientForm'
